import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Rocket,
  Folder,
  FileText,
  Sparkles,
  FileCheck,
  MessageSquare,
  User,
  Plus,
  Upload,
  BarChart3,
  Activity,
  Target,
  Scale,
  Search,
  AlertTriangle,
  ArrowRight,
  ShieldCheck,
  BookOpen,
  Gavel,
  ClipboardList,
  Eye,
  Bell,
  Settings,
} from 'lucide-react';

interface HelpSection {
  id: string;
  icon: React.ReactNode;
  title: string;
  content: React.ReactNode;
}

const CollapsibleSection: React.FC<{ section: HelpSection; isOpen: boolean; onToggle: () => void }> = ({ section, isOpen, onToggle }) => (
  <div className="card dark:bg-medical-800 dark:border-medical-700">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between text-left"
    >
      <div className="flex items-center space-x-3">
        <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
          {section.icon}
        </div>
        <h3 className="text-lg font-semibold text-medical-900 dark:text-medical-100">{section.title}</h3>
      </div>
      {isOpen
        ? <ChevronUp className="h-5 w-5 text-medical-400" />
        : <ChevronDown className="h-5 w-5 text-medical-400" />
      }
    </button>
    {isOpen && (
      <div className="mt-4 pt-4 border-t border-medical-100 dark:border-medical-700 text-medical-700 dark:text-medical-300 text-sm leading-relaxed space-y-4">
        {section.content}
      </div>
    )}
  </div>
);

export const HelpPage: React.FC = () => {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['getting-started']));

  const toggleSection = (id: string) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sections: HelpSection[] = [
    {
      id: 'getting-started',
      icon: <Rocket className="h-5 w-5" />,
      title: 'Getting Started',
      content: (
        <>
          <p>ClinLexis helps you analyse medical evidence and generate court-ready medico-legal reports. Here's the typical workflow:</p>
          <div className="space-y-3 mt-3">
            {[
              { step: '1', icon: <Plus className="h-4 w-4" />, title: 'Create a Case', desc: 'Go to Cases > New Case. Enter the case name, patient details, and select the case type (e.g. Medical Negligence, Clinical Review).' },
              { step: '2', icon: <Upload className="h-4 w-4" />, title: 'Upload Evidence', desc: 'In the Records tab, drag and drop your medical records, scans, and clinical documents. PDFs, images, and scanned documents are all supported with automatic OCR.' },
              { step: '3', icon: <Sparkles className="h-4 w-4" />, title: 'Review AI Analysis', desc: 'Once documents are processed, review extracted findings across dedicated tabs: Chronology, Clinical Standards, Causation, Evidence Integrity, Body Map, and more. Star key findings as you go.' },
              { step: '4', icon: <FileCheck className="h-4 w-4" />, title: 'Drafting', desc: 'Use the Drafting tab in the right sidebar. Choose a template — reports, letters of instruction, letters of claim, or case screenings — answer a few preference questions, and the AI will compile a comprehensive draft with full source citations.' },
            ].map(item => (
              <div key={item.step} className="flex items-start space-x-3 p-3 bg-medical-50 dark:bg-medical-700/30 rounded-lg">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center text-xs font-bold">
                  {item.step}
                </div>
                <div>
                  <p className="font-medium text-medical-900 dark:text-medical-100">{item.title}</p>
                  <p className="text-medical-600 dark:text-medical-400 mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Link to="/cases" className="btn-primary inline-flex items-center space-x-2">
              <span>Go to Cases</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </>
      ),
    },
    {
      id: 'cases',
      icon: <Folder className="h-5 w-5" />,
      title: 'Cases',
      content: (
        <>
          <p>Cases are the core organisational unit. Each case represents a medico-legal matter with its own evidence, analysis, and reports.</p>
          <div className="space-y-2 mt-3">
            <p><strong className="text-medical-900 dark:text-medical-100">Creating a case:</strong> Navigate to <Link to="/cases" className="text-primary-600 dark:text-primary-400 hover:underline">Cases</Link> and click <strong>New Case</strong>. You can rename the case and configure case types in the Case Brief section.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Case types:</strong> Medical Negligence, Clinical Review, Expert Opinion, Tribunal, Inquest, Personal Injuries, Other, and Exploratory. These help the system select appropriate templates and processing approaches.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Case statuses:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Created</strong> &mdash; Case exists but no documents uploaded yet</li>
              <li><strong>Uploading</strong> &mdash; Documents are being uploaded to the case</li>
              <li><strong>Processing</strong> &mdash; AI is actively extracting and analysing documents</li>
              <li><strong>Ready</strong> &mdash; Processing complete, case is ready for review and report generation</li>
              <li><strong>Review</strong> &mdash; Case is under active review</li>
              <li><strong>Complete</strong> &mdash; Case work is finished</li>
              <li><strong>Error</strong> &mdash; Something went wrong during processing; you can retry or contact support</li>
            </ul>
            <p><strong className="text-medical-900 dark:text-medical-100">Processing guidance:</strong> You can configure date ranges and analysis focus to direct the AI's attention to the most relevant period and topics.</p>
          </div>
        </>
      ),
    },
    {
      id: 'evidence',
      icon: <FileText className="h-5 w-5" />,
      title: 'Evidence & Documents',
      content: (
        <>
          <p>Upload and manage all medical evidence within each case.</p>
          <div className="space-y-2 mt-3">
            <p><strong className="text-medical-900 dark:text-medical-100">Supported formats:</strong> PDF documents, scanned images (JPEG, PNG), and multi-page medical records. OCR is applied automatically to scanned documents.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Uploading:</strong> Open your case, go to the Records tab, and drag and drop files or click to browse. Batch uploading is supported.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">What the AI extracts:</strong> Patients, medical events (medications, diagnoses, procedures, investigations), dates with certainty levels, and document types (clinic letters, discharge summaries, nursing notes, investigation reports).</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Viewing documents:</strong> Click any document to open it in the Medical Records panel on the right. You can search text, zoom, and use area-zoom to focus on specific regions.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Reading progress:</strong> ClinLexis tracks which pages you have reviewed for each document, showing a reading progress percentage. This helps ensure you have thoroughly reviewed all evidence before generating reports.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Bookmarks:</strong> Bookmark important pages and add notes to them for quick reference later.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Key findings:</strong> Star important findings as you review. Starred findings can be compiled into a dedicated Key Findings Report.</p>
          </div>
        </>
      ),
    },
    {
      id: 'analysis',
      icon: <Sparkles className="h-5 w-5" />,
      title: 'AI Analysis',
      content: (
        <>
          <p>Create analysis objectives to direct the AI's investigation of your evidence. Each objective type focuses on a different aspect of the case.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            {[
              { icon: <BarChart3 className="h-4 w-4" />, label: 'Chronology', desc: 'Build a timeline of medical events from your evidence, with dates and source citations.', color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-900/20' },
              { icon: <Activity className="h-4 w-4" />, label: 'Standard of Care', desc: 'Compare treatment against clinical guidelines and established best practices.', color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20' },
              { icon: <Target className="h-4 w-4" />, label: 'Causation Analysis', desc: 'Analyse cause-and-effect relationships between events in the case.', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20' },
              { icon: <Scale className="h-4 w-4" />, label: 'Comparative Analysis', desc: 'Compare different treatment approaches, outcomes, or expert opinions.', color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/20' },
              { icon: <Search className="h-4 w-4" />, label: 'Gap Analysis', desc: 'Identify gaps in medical documentation, missing records, and temporal voids.', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20' },
              { icon: <AlertTriangle className="h-4 w-4" />, label: 'Inconsistency Detection', desc: 'Detect contradictions and conflicts across medical documents and records.', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20' },
              { icon: <ShieldCheck className="h-4 w-4" />, label: 'Guideline Compliance', desc: 'Assess whether clinical actions complied with relevant NICE, SIGN, or other clinical guidelines.', color: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-900/20' },
              { icon: <BookOpen className="h-4 w-4" />, label: 'Expert Opinion Support', desc: 'Gather and organise evidence to support expert witness opinion formation.', color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/20' },
              { icon: <Gavel className="h-4 w-4" />, label: 'Court Bundle Preparation', desc: 'Organise and index evidence for court bundle compilation.', color: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-50 dark:bg-slate-900/20' },
              { icon: <ClipboardList className="h-4 w-4" />, label: 'Issue-Focused Summary', desc: 'Generate a focused summary addressing specific issues or questions in the case.', color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-900/20' },
            ].map(item => (
              <div key={item.label} className={`p-3 rounded-lg ${item.bg}`}>
                <div className={`flex items-center space-x-2 ${item.color} font-medium mb-1`}>
                  {item.icon}
                  <span>{item.label}</span>
                </div>
                <p className="text-medical-600 dark:text-medical-400 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-2">
            <p><strong className="text-medical-900 dark:text-medical-100">Creating an objective:</strong> Open your case, go to the Objectives tab, and click "New Objective". The wizard guides you through selecting the type, relevant guidelines, patients, and date ranges.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Reviewing results:</strong> Analysis results are presented across dedicated tabs within the case detail view:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Overview</strong> &mdash; A dashboard summary of the case and its findings</li>
              <li><strong>Claimant</strong> &mdash; Background information on the claimant</li>
              <li><strong>Chronology</strong> &mdash; Interactive timeline of medical events</li>
              <li><strong>Expert Evidence</strong> &mdash; Expert witness support materials</li>
              <li><strong>Images</strong> &mdash; Medical images extracted from documents</li>
              <li><strong>Body Map</strong> &mdash; Anatomical diagram with clinical findings mapped to body regions</li>
              <li><strong>Clinical Standards</strong> &mdash; Standard of care and guideline compliance analysis</li>
              <li><strong>Evidence Integrity</strong> &mdash; Inconsistencies, contradictions, and documentation gaps</li>
              <li><strong>Causation</strong> &mdash; Cause-and-effect analysis between medical events</li>
            </ul>
            <p>All results include full citations back to source documents and page numbers. Click any citation to jump directly to the relevant passage.</p>
          </div>
        </>
      ),
    },
    {
      id: 'reports',
      icon: <FileCheck className="h-5 w-5" />,
      title: 'Drafts',
      content: (
        <>
          <p>Draft court-ready medico-legal documents — expert reports, letters of instruction, letters of claim, and case screenings — from your case analysis.</p>
          <div className="space-y-2 mt-3">
            <p><strong className="text-medical-900 dark:text-medical-100">Starting a draft:</strong> Open a case and use the <em>Drafting</em> tab in the right sidebar. Choose from built-in templates (Key Findings Report, Chronology Summary, Expert Witness Report, Letter of Instruction, Letter of Claim, Case Screening, and more) or your own custom templates. Answer a few preference questions, and the AI will generate your draft section by section.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Custom templates:</strong> Create your own templates under <Link to="/templates" className="text-primary-600 dark:text-primary-400 hover:underline">Templates</Link>, or upload an existing Word document and ClinLexis will extract its section structure automatically.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Editing:</strong> After generation, use the editor to refine content. The ClinLexis Assistant can help revise sections or generate additional content.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Version history:</strong> Every edit is tracked with full version history. You can view previous versions and revert to any earlier state if needed.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Exporting:</strong> Export finalised drafts to PDF, Word, or plain text format with preserved formatting and citations.</p>
          </div>
        </>
      ),
    },
    {
      id: 'ai-assistant',
      icon: <MessageSquare className="h-5 w-5" />,
      title: 'ClinLexis Assistant',
      content: (
        <>
          <p>The ClinLexis Assistant is available throughout the application via the right sidebar.</p>
          <div className="space-y-2 mt-3">
            <p><strong className="text-medical-900 dark:text-medical-100">Opening the assistant:</strong> Click the <em>Case Assistant</em> tab in the right sidebar, or press <kbd className="px-1.5 py-0.5 rounded bg-medical-100 dark:bg-medical-700 text-xs font-mono">&#8984;+Shift+A</kbd> (Mac) or <kbd className="px-1.5 py-0.5 rounded bg-medical-100 dark:bg-medical-700 text-xs font-mono">Ctrl+Shift+A</kbd> (Windows/Linux).</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Modes:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Ask</strong> &mdash; Query your case documents with natural language questions. Answers include citations to source documents. Available on all pages.</li>
              <li><strong>Revise</strong> &mdash; Highlight text in the report editor and ask the AI to revise or rephrase specific sections. Available in the report editor.</li>
              <li><strong>Write</strong> &mdash; Generate new content for report sections based on your case evidence. Available in the report editor.</li>
            </ul>
            <p><strong className="text-medical-900 dark:text-medical-100">Multi-tab conversations:</strong> You can open multiple concurrent conversations with the assistant, each with its own context. Conversation layouts are saved per case.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Context:</strong> The assistant is context-aware. It knows which case, documents, and report section you're currently working with. In Revise and Write modes, you can also select specific documents, guidelines, images, and objectives as additional context.</p>
          </div>
        </>
      ),
    },
    {
      id: 'reviewing',
      icon: <Eye className="h-5 w-5" />,
      title: 'Reviewing Evidence',
      content: (
        <>
          <p>ClinLexis provides several tools to help you systematically review case evidence.</p>
          <div className="space-y-2 mt-3">
            <p><strong className="text-medical-900 dark:text-medical-100">Right sidebar:</strong> The case view has a three-tab sidebar on the right:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Medical Records</strong> &mdash; Browse and view documents in the case</li>
              <li><strong>Case Assistant</strong> &mdash; AI-powered Q&amp;A about your case documents</li>
              <li><strong>Report Drafting</strong> &mdash; Generate and manage reports</li>
            </ul>
            <p><strong className="text-medical-900 dark:text-medical-100">Reading progress:</strong> As you view document pages, ClinLexis tracks your progress and displays a review percentage for each document and the overall case.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Key findings:</strong> Star important findings across any tab. Starred findings are collected and can be used to generate a dedicated Key Findings Report summarising your most important observations.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Body map:</strong> The Body Map tab provides an anatomical diagram with clinical findings (diagnoses, medications, procedures, symptoms, investigations, vital signs) mapped to the relevant body region. Click any finding to jump to its source document.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Medical images:</strong> The Images tab automatically extracts and displays images from your uploaded medical records for quick visual review.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Citations:</strong> All AI-generated findings include citations linking back to the source document and page number. Click any citation to open the document at the exact passage.</p>
          </div>
        </>
      ),
    },
    {
      id: 'account',
      icon: <User className="h-5 w-5" />,
      title: 'Account & Billing',
      content: (
        <>
          <div className="space-y-2">
            <p><strong className="text-medical-900 dark:text-medical-100">Profile &amp; Billing:</strong> Manage your subscription, payment methods, credit balance, and transaction history under <Link to="/account" className="text-primary-600 dark:text-primary-400 hover:underline">Account</Link>.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Professional profile:</strong> Add your professional credentials, registration body (GMC, NMC, HCPC, SRA, etc.), registration number, specialisations, memberships, and notable appointments under <Link to="/account?tab=credentials" className="text-primary-600 dark:text-primary-400 hover:underline">Account &gt; Professional Profile</Link>. You can also upload your CV for automatic parsing.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Activity log:</strong> Review a full audit trail of all actions &mdash; creations, modifications, deletions, and reversions &mdash; with before/after diffs, under <Link to="/account?tab=activity" className="text-primary-600 dark:text-primary-400 hover:underline">Account &gt; Activity Log</Link>. Filter by date range, status, or case.</p>
          </div>
        </>
      ),
    },
    {
      id: 'settings',
      icon: <Settings className="h-5 w-5" />,
      title: 'Settings',
      content: (
        <>
          <div className="space-y-2">
            <p>Configure your application preferences under <Link to="/settings" className="text-primary-600 dark:text-primary-400 hover:underline">Settings</Link>.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Theme:</strong> Switch between light and dark mode.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Date format:</strong> Choose your preferred date display format (DD/MM/YYYY, DD Mon YYYY, or YYYY-MM-DD). This applies across the entire application.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Redaction mode:</strong> Toggle redaction to blur patient names, dates of birth, and personal information &mdash; useful when screen-sharing or working in shared spaces. Hover over blurred text to temporarily reveal it. Toggle with <kbd className="px-1.5 py-0.5 rounded bg-medical-100 dark:bg-medical-700 text-xs font-mono">&#8984;+Shift+P</kbd> (Mac) or <kbd className="px-1.5 py-0.5 rounded bg-medical-100 dark:bg-medical-700 text-xs font-mono">Ctrl+Shift+P</kbd> (Windows/Linux), or via the toggle in the left sidebar.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Notifications:</strong> Configure how you are notified when analysis completes &mdash; via in-app notifications, email, or SMS. Particularly useful for large document bundles that take time to process.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Two-factor authentication:</strong> Set up and manage two-factor authentication (MFA) for additional account security under <Link to="/settings/mfa" className="text-primary-600 dark:text-primary-400 hover:underline">Settings &gt; MFA</Link>.</p>
          </div>
        </>
      ),
    },
    {
      id: 'notifications',
      icon: <Bell className="h-5 w-5" />,
      title: 'Notifications',
      content: (
        <>
          <div className="space-y-2">
            <p>Access your notifications via the <Link to="/notifications" className="text-primary-600 dark:text-primary-400 hover:underline">Notifications</Link> page.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Inbox:</strong> View all notifications including case updates, system alerts, and security alerts. Search, filter by category, and mark as read or unread.</p>
            <p><strong className="text-medical-900 dark:text-medical-100">Preferences:</strong> Control which notifications you receive by email, desktop, or in-app, and choose your preferred frequency (immediate, hourly digest, or daily digest).</p>
            <p><strong className="text-medical-900 dark:text-medical-100">History:</strong> View notification statistics and recent activity.</p>
          </div>
        </>
      ),
    },
    {
      id: 'keyboard-shortcuts',
      icon: <ArrowRight className="h-5 w-5" />,
      title: 'Keyboard Shortcuts',
      content: (
        <>
          <div className="space-y-2">
            <p>Speed up your workflow with these keyboard shortcuts:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              {[
                { keys: '\u2318+Shift+A / Ctrl+Shift+A', action: 'Toggle ClinLexis Assistant' },
                { keys: '\u2318+Shift+P / Ctrl+Shift+P', action: 'Toggle redaction mode' },
              ].map(item => (
                <div key={item.keys} className="flex items-center justify-between p-2 bg-medical-50 dark:bg-medical-700/30 rounded-lg">
                  <span className="text-medical-600 dark:text-medical-400 text-xs">{item.action}</span>
                  <kbd className="px-1.5 py-0.5 rounded bg-medical-100 dark:bg-medical-700 text-xs font-mono text-medical-900 dark:text-medical-100">{item.keys}</kbd>
                </div>
              ))}
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto dark:bg-medical-900">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <HelpCircle className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          <h1 className="text-2xl font-bold text-medical-900 dark:text-medical-100">Help Centre</h1>
        </div>
        <p className="text-medical-600 dark:text-medical-400">
          Everything you need to navigate ClinLexis's features and get the most from your medico-legal analysis.
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {sections.map(section => (
          <CollapsibleSection
            key={section.id}
            section={section}
            isOpen={openSections.has(section.id)}
            onToggle={() => toggleSection(section.id)}
          />
        ))}
      </div>
    </div>
  );
};
